import React from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const Home = () => {
  const navigate = useNavigate();
  const handleFreeConsultation = () => navigate('/contact');
  return (
    <>
      {/* HERO SECTION */}
      <section className="min-h-screen bg-gradient-to-br from-[#111111] via-[#1a1a1a] to-[#111111] flex items-center">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT CONTENT */}
          <div>
            <h1 className="text-5xl md:text-7xl font-extrabold leading-tight text-white tracking-tight">
              Simple.<br />
              <span className="text-[#d97925] italic">Secure.</span><br />
              Seamless.
            </h1>

            <p className="mt-6 text-lg text-white leading-relaxed max-w-xl">
              TickBox Technologies combines{" "}
              
                deep regulatory <strong><span className="text-[#d97925]">expertise</span>
              </strong>
              , <strong><span className="text-[#d97925]">intelligent</span></strong> digital
              platforms, and <strong><span className="text-[#d97925]">proactive</span></strong> {" "}
              service delivery to protect your business, reduce risk, and enable
              sustainable growth —{" "}
              <strong>across every state, every sector.</strong>
            </p>

            {/* BUTTONS */}
            <div className="flex flex-wrap gap-5 mt-8">
             <div className="flex flex-wrap gap-5 mt-8">
  <button
    onClick={() => navigate("/products")}
    className="px-8 py-4 bg-[#d97925] text-white font-semibold rounded-xl shadow-lg hover:bg-orange-600 transition"
  >
    See Our Products
  </button>

  <button
    onClick={() => navigate("/services")}
    className="px-8 py-4 border border-zinc-300  text-[#d97925] rounded-xl font-semibold flex items-center gap-2 hover:bg-zinc-100 transition"
  >
    Explore Services <ArrowRight size={18} />
  </button>
</div>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-14">
              <div>
                <div className="text-3xl font-bold text-[#d97925]">18+</div>
                <div className="text-sm text-white mt-1">
                  Register Handling Locations
                </div>
              </div>

              <div>
                <div className="text-3xl font-bold text-[#d97925]">9</div>
                <div className="text-sm text-white mt-1">
                  Service Domains
                </div>
              </div>

              <div>
                <div className="text-3xl font-bold text-[#d97925]">4</div>
                <div className="text-sm text-white mt-1">
                  Live Products
                </div>
              </div>

              <div>
                <div className="text-3xl font-bold text-[#d97925]">24/7</div>
                <div className="text-sm text-white mt-1">
                  Support
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT DASHBOARD */}
          <div className="relative">
            <div className="bg-black text-white rounded-3xl p-8 shadow-2xl">

              {/* HEADER */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">
                  Compliance Dashboard
                </h3>
                <span className="text-green-400 text-sm">
                  ● All Systems Active
                </span>
              </div>

              {/* METRICS */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <div className="text-orange-400 text-xl font-bold">99.8%</div>
                  <div className="text-xs text-zinc-400">
                    Compliance Score
                  </div>
                </div>

                <div>
                  <div className="text-green-400 text-xl font-bold">1,248</div>
                  <div className="text-xs text-zinc-400">
                    Mandates Tracked
                  </div>
                </div>

                <div>
                  <div className="text-white text-xl font-bold">0</div>
                  <div className="text-xs text-zinc-400">
                    Pending Penalties
                  </div>
                </div>
              </div>

              {/* LIST */}
              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between">
                  <span>PF Monthly Returns</span>
                  <span className="text-green-400">Filed ✓</span>
                </div>
                <div className="flex justify-between">
                  <span>ESI Contributions</span>
                  <span className="text-green-400">Submitted ✓</span>
                </div>
                <div className="flex justify-between">
                  <span>Factory Act Renewals</span>
                  <span className="text-yellow-400">In Progress</span>
                </div>
                <div className="flex justify-between">
                  <span>CLRA Registers</span>
                  <span className="text-green-400">Updated ✓</span>
                </div>
              </div>

              {/* PROGRESS */}
              <div className="space-y-3 text-sm">
                {[
                  { label: "Filing accuracy", value: 99 },
                  { label: "State coverage", value: 82 },
                  { label: "On-time rate", value: 100 },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span>{item.label}</span>
                      <span>{item.value}%</span>
                    </div>
                    <div className="w-full bg-zinc-700 h-2 rounded-full">
                      <div
                        className="bg-[#d97925] h-2 rounded-full"
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* BADGES */}

{/* BADGE 1 */}
<div className="absolute -top-10 -left-8 bg-white rounded-xl px-4 py-3 shadow-lg float-anim glow border border-[#d97925]">
  <div className="text-lg">🛡️</div>
  <div className="text-xs font-semibold">Zero Penalties</div>
  <div className="text-[10px] text-zinc-500 mt-1">
    All filings on-time
  </div>
</div>

{/* BADGE 2 */}
<div className="absolute -bottom-12 -right-10 bg-white rounded-xl px-4 py-3 shadow-lg float-anim glow border border-[#d97925]">
  <div className="text-lg">🔒</div>
  <div className="text-xs font-semibold">AES Secured</div>
  <div className="text-[10px] text-zinc-500 mt-1">
    Enterprise encryption
  </div>
</div>
          </div>
        </div>
      </section>

      {/* ORANGE STATS STRIP */}
      <section className="w-full bg-[#d97925] text-white">
        <div className="grid grid-cols-2 md:grid-cols-4 text-center">

          <div className="py-12 border-r border-white/20">
            <h2 className="text-4xl font-bold">2000+</h2>
            <p className="text-sm mt-2 opacity-90">
              Register Handling Locations
            </p>
          </div>

          <div className="py-12 border-r border-white/20">
            <h2 className="text-4xl font-bold">100000+</h2>
            <p className="text-sm mt-2 opacity-90">
              Overall Records Handled
            </p>
          </div>

          <div className="py-12 border-r border-white/20">
            <h2 className="text-4xl font-bold">500+</h2>
            <p className="text-sm mt-2 opacity-90">
              Overall Returns Handled
            </p>
          </div>

          <div className="py-12">
            <h2 className="text-4xl font-bold">&lt;2hrs</h2>
            <p className="text-sm mt-2 opacity-90">
              Least TAT
            </p>
          </div>

        </div>
      </section>

        <section className="py-28  bg-gradient-to-br from-[#111111] to-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-5xl font-bold tracking-tight mb-8"
          >
            Ready to Experience the Difference
          </motion.h2>

          <p className="text-2xl mb-12 max-w-3xl mx-auto opacity-90">
           Proactive compliance. Transparent processes. Technology-driven results.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleFreeConsultation}
            className="inline-flex items-center gap-4 px-12 py-7 bg-white text-[#d97925] font-bold text-2xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-colors"
          >
            Get Started with TickBox
            <ArrowRight size={32} />
          </motion.button>
        </div>
      </section>

      <footer className="bg-white text-black pt-16 pb-8">
  <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-12">

    {/* BRAND */}
    <div>
      <img
        src="/Assets/Images/icon.png" 
        alt="TickBox Technologies"
        className="h-[50px] w-[160px] object-contain mb-4"
      />
      <p className="text-sm text-black leading-relaxed">
        India's premium statutory compliance partner — simplifying regulatory
        requirements for businesses of all sizes across 18+ states.
      </p>

      {/* LINKEDIN */}
      <div className="flex items-center gap-3 mt-5">
        <div className="bg-[#0A66C2] text-white font-bold text-xs w-5 h-5 flex items-center justify-center rounded">
          in
        </div>
        <a
          href="https://www.linkedin.com/company/tickbox-technologies/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm hover:text-[#d97925]"
        >
          Follow us on LinkedIn
        </a>
      </div>
    </div>

    {/* SERVICES */}
    <div>
      <h4 className="font-semibold mb-4 text-lg">Services</h4>
      <ul className="space-y-2 text-sm text-black">
        <li><a href="/services">Establishment Compliance</a></li>
        <li><a href="/services">Factory Compliance</a></li>
        <li><a href="/services">Contract Labour / CLRA</a></li>
        <li><a href="/services">Payroll Services</a></li>
        <li><a href="/services">Payroll Compliance</a></li>
        <li><a href="/services">Industrial Licensing</a></li>
        <li><a href="/services">Consulting & Audit</a></li>
         <li><a href="/services">Inspection Handling</a></li>
        <li><a href="/services">Compliance Legal Services</a></li>
        
         
         
      </ul>
    </div>

    {/* PRODUCTS */}
    <div>
      <h4 className="font-semibold mb-4 text-lg">Products</h4>
      <ul className="space-y-2 text-sm text-black">
        <li><a href="/products">H-Square</a></li>
        <li><a href="/products">R-Cube</a></li>
      </ul>
    </div>

    {/* COMPANY */}
    <div>
      <h4 className="font-semibold mb-4 text-lg">Company</h4>
      <ul className="space-y-2 text-sm text-black">
        <li><a href="/products">Products</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/whyus">Why TickBox</a></li>
        <li><a href="/resources">Resources</a></li>
        <li><a href="/about">About Us</a></li>
        <li><a href="/careers">Careers</a></li>
        <li><a href="/contact">Contact</a></li>
        
        
        <li>
  <a
    href="https://mail.google.com/mail/?view=cm&fs=1&to=administrator@tickbox.co.in"
    target="_blank"
    rel="noopener noreferrer"
  >
    administrator@tickbox.co.in
  </a>
</li>
      </ul>
    </div>

  </div>

  {/* BOTTOM */}
  {/* BOTTOM */}
<div className="border-t border-black/20 mt-12 pt-6 px-6">
  <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-black/60">

    {/* LEFT */}
    <div className="text-left">
      © 2026 TickBox Technologies. All rights reserved.
    </div>

    {/* CENTER */}
    <div className="text-center">
      Madurai, Tamil Nadu · +91 93857 94811 · www.tickbox.co.in
    </div>

    {/* RIGHT */}
    <div className="text-right">
      Simple · Secure · Seamless
    </div>

  </div>
</div>

{/* DISCLAIMER */}
<div className="border-t border-black/10 mt-6 pt-6">
  <div className="max-w-6xl mx-auto px-6 text-xs text-black/60 leading-relaxed text-left md:text-center">
    TickBox Technologies provides statutory compliance management services and
    technology platforms. Information on this website is for general informational
    purposes only and does not constitute legal advice. TickBox recommends
    consulting qualified legal counsel for jurisdiction-specific matters. All
    client data is encrypted using AES standards and used solely for contracted
    services. TickBox does not sell, rent, or share client data with third parties.
  </div>
</div>
</footer>
      </>
    
  );
};

export default Home;